/**
 * As saídas que estão esperando decisão, e o botão de aprovar.
 *
 * Etapa 3 da parte 5. Ela resolve uma coisa só, e é uma coisa que hoje não existe: enxergar,
 * de fora da congregação, o que está parado lá dentro. A tesouraria lança uma saída que passa
 * do limite de quem lançou, ela fica pendente, e o dinheiro não anda até alguém com permissão
 * abrir o notebook daquela congregação.
 *
 * **Aprovar pede a senha**, e acontece em dois passos: primeiro o botão, depois a confirmação.
 * Nenhuma aprovação fica a um toque de distância na primeira vez. **A senha confirmada vale por
 * vinte minutos**, e nesse intervalo as seguintes são um toque: decisão do Cristiano, de
 * 08/10/2026, depois de testar no celular.
 *
 * **Recusar não existe aqui**, e isso é decisão, não esquecimento: o painel só aprova, e recusar
 * continua sendo no notebook.
 *
 * **Quem não pode aprovar lê o motivo no lugar do botão**, e o motivo vem escrito do servidor.
 *
 * **A ordem é a da espera, não a do dinheiro.** A mais antiga primeiro, porque a pergunta que
 * esta tela responde é "o que está parado há mais tempo", e não "qual é a maior".
 *
 * **O que esta lista não mostra é o nome de quem recebeu o dinheiro.** A descrição, sim, e ela
 * vem cortada com reticências quando não cabe. O porquê das duas coisas está em
 * `src/painel/saidas-pendentes.ts`, do lado do servidor, que é quem decide o que vem.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type SaidaAprovada, type SaidaEsperando, type SaidasEsperando
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { emReais } from '../dinheiro'

/** `2026-08-20` vira `20/08/2026`. A data do gasto é texto, e não hora: não passa por `Date`. */
function dia(data: string): string {
  const partes = data.slice(0, 10).split('-')
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : data
}

/** "há 3 dias", e "hoje" quando é hoje. Plural de um dia é a coisa que mais salta aos olhos. */
function aEspera(dias: number): string {
  if (dias <= 0) return 'lançada hoje'
  if (dias === 1) return 'esperando há 1 dia'
  return `esperando há ${dias} dias`
}

export function Saidas({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [dados, setDados] = useState<SaidasEsperando | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  /** Qual saída está com o formulário aberto. Uma de cada vez, nunca duas. */
  const [aprovando, setAprovando] = useState<string | null>(null)
  const [feito, setFeito] = useState<SaidaAprovada | null>(null)

  /**
   * Qual pedido é o mais novo. **Resposta velha que chega atrasada não pinta a tela.**
   *
   * Sem isto, duas buscas em voo podem terminar fora de ordem, e a última a chegar vence mesmo
   * sendo a mais velha. É fácil de acontecer logo depois de aprovar: a busca de antes da
   * aprovação ainda está na rua, a nova volta primeiro, e a velha traz a saída aprovada de volta
   * para a lista. Quem está olhando vê a linha ressuscitar sozinha.
   *
   * `useRef` e não estado: ele muda no meio de um evento e ninguém o desenha.
   */
  const pedidoMaisNovo = useRef(0)

  /**
   * A tela já tem uma lista desenhada?
   *
   * Decide o que dizer quando uma busca falha, e a diferença importa: sem lista nenhuma, a tela
   * está vazia e "não foi possível carregar" é a notícia inteira. COM lista, o que falhou foi só
   * a atualização, e dizer "não foi possível carregar" em cima de uma aprovação que acabou de dar
   * certo faz a pessoa achar que a aprovação é que falhou. Ela não falhou: o recado verde ao lado
   * veio do servidor.
   */
  const temLista = useRef(false)

  /**
   * QUANDO A JANELA DA SENHA TERMINA, pelo relógio desta máquina. Nulo quando ela está fechada.
   *
   * **O aviso de cima tem de acompanhar o relógio, e não a hora em que a tela carregou.** Achado
   * TEL-001 da auditoria, de 13/10/2026: o servidor respondia "ainda 17 minutos", a tela escrevia
   * aquilo e não mexia mais. Vinte minutos depois, o formulário de baixo já pedia a senha outra
   * vez, e o aviso de cima continuava prometendo que cada aprovação era um toque. Nenhum dinheiro
   * andou sem senha, porque quem decide é o servidor; o que estava errado era a tela.
   *
   * O servidor diz quantos minutos sobram, e daqui para a frente a contagem é desta tela. Ela
   * não precisa acertar o segundo: ela precisa nunca prometer o que já venceu.
   */
  const [aJanelaTerminaEm, setAJanelaTerminaEm] = useState<number | null>(null)

  /** O relógio desta tela. Ele anda sozinho, e só enquanto há janela para contar. */
  const [agora, setAgora] = useState(() => Date.now())

  /**
   * O que o servidor disse sobre a janela vira uma HORA DE TÉRMINO.
   *
   * `minutos_que_a_senha_vale` chega arredondado para cima, então esta conta pode errar por menos
   * de um minuto. Quem conserta esse minuto é o próprio servidor: quem tentar aprovar depois do
   * prazo recebe `senha_necessaria`, e aí a tela fecha a janela na hora, sem esperar relógio.
   */
  const anotarAJanela = useCallback(
    (janela: { a_senha_ja_vale: boolean; minutos_que_a_senha_vale: number }) => {
      const neste = Date.now()
      setAgora(neste)
      setAJanelaTerminaEm(
        janela.a_senha_ja_vale ? neste + janela.minutos_que_a_senha_vale * 60_000 : null
      )
    },
    []
  )

  /**
   * A JANELA FECHOU, e quem descobriu foi o servidor.
   *
   * A segunda metade do TEL-001, e a que não depende de relógio nenhum. Um aviso que só conta o
   * tempo desta máquina ainda mente quando o relógio está errado, quando o computador dormiu ou
   * quando a sessão foi derrubada do outro lado. Então, no instante em que QUALQUER linha ouve do
   * servidor que a senha é necessária outra vez, a janela fecha para a tela inteira: é uma janela
   * só, da sessão, e não uma por saída.
   */
  const aJanelaFechou = useCallback(() => setAJanelaTerminaEm(null), [])

  const carregar = useCallback(() => {
    const meu = ++pedidoMaisNovo.current
    setErro(null)
    servidor
      .saidasPendentes()
      .then((novos) => {
        if (meu !== pedidoMaisNovo.current) return
        temLista.current = true
        setDados(novos)
        anotarAJanela(novos)
      })
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        if (meu !== pedidoMaisNovo.current) return
        if (temLista.current) {
          return setErro(
            'Não consegui atualizar a lista agora. O que está na tela pode estar atrasado; ' +
            'recarregue a página daqui a pouco.'
          )
        }
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
  }, [aoSair, anotarAJanela])

  useEffect(carregar, [carregar])

  /**
   * O relógio da tela, de quinze em quinze segundos, e só enquanto sobra janela para contar.
   *
   * `setTimeout` que se remarca, e não `setInterval`: quando a janela vence, a conta para sozinha
   * e a tela deixa de acordar de graça. Quinze segundos bastam para um aviso que fala de minutos.
   */
  useEffect(() => {
    if (aJanelaTerminaEm === null || aJanelaTerminaEm <= agora) return
    const relogio = setTimeout(() => setAgora(Date.now()), 15_000)
    return () => clearTimeout(relogio)
  }, [aJanelaTerminaEm, agora])

  /** Quantos minutos a senha ainda vale, pela conta desta tela. Zero quando a janela fechou. */
  const minutosQueASenhaVale =
    aJanelaTerminaEm === null ? 0 : Math.max(0, Math.ceil((aJanelaTerminaEm - agora) / 60_000))

  /**
   * A senha ainda vale?
   *
   * **Isto é só o que a tela desenha.** Quem decide se uma aprovação vai sem senha continua
   * sendo o servidor, a cada pedido. Esta conta erra para o lado seguro: ela fecha a janela antes
   * da hora quando o relógio desta máquina está adiantado, e o resultado de errar é pedir a
   * senha de novo, que é o que já acontecia na primeira aprovação.
   */
  const aSenhaJaVale = minutosQueASenhaVale > 0

  /**
   * A saída aprovada sai da lista NA HORA, e a lista é pedida de novo logo atrás.
   *
   * **Nasceu de uma prova instável, e o defeito que ela escondia é de tela.** Até 13/10/2026 a
   * tela só pedia a lista de novo: entre o recado verde e a resposta do servidor, a saída
   * aprovada continuava na lista, com o botão de aprovar e tudo. Numa internet boa isso dura um
   * piscar; no celular do pastor, no meio de um culto, dura o suficiente para ele tocar em
   * "Aprovar esta saída" de novo e ler que ela já foi decidida.
   *
   * Era também o que deixava a prova do navegador instável: ela esperava a lista encolher, e a
   * lista só encolhia quando a segunda resposta chegasse. Numa máquina cheia, a espera estourava
   * e a prova acusava "quatro, esperava três", que é o sintoma do achado da auditoria da Astra
   * em `painel-no-navegador.test.ts`. Tirar a linha na hora acaba com a corrida: não existe mais
   * instante nenhum em que a tela mostre como pendente uma saída que ela acabou de aprovar.
   *
   * **A conta é refeita junto**, e não só a lista: o total, a soma parada, o aviso do comprovante
   * e quantas você pode aprovar. Deixar os números velhos ao lado da lista nova seria trocar um
   * desencontro por outro. O pedido logo atrás é quem põe tudo de pé com o número do servidor,
   * inclusive se ele discordar.
   *
   * **A janela da senha NÃO mora aqui.** Ela tem uma dona só, `anotarAJanela`, chamada com a
   * resposta da aprovação e com a resposta da lista. Guardar a mesma verdade em dois lugares foi
   * exatamente o que produziu o achado TEL-001: a tela escrevia um prazo que ninguém mais
   * recontava.
   */
  const tirarDaLista = useCallback((feita: SaidaAprovada) => {
    /*
     * A busca que estava em voo deixa de valer aqui.
     *
     * A aprovação é o fato mais recente que esta tela conhece: uma lista pedida ANTES dela não
     * pode sobrescrever o que ela acabou de mudar, nem vindo sem erro nenhum.
     */
    pedidoMaisNovo.current += 1
    setDados((atual) => {
      if (atual === null) return atual
      const ficam = atual.saidas.filter((s) => s.id !== feita.id)
      if (ficam.length === atual.saidas.length) return atual

      return {
        ...atual,
        saidas: ficam,
        total: ficam.length,
        soma: ficam.reduce((s, x) => s + x.valor, 0),
        sem_o_comprovante_exigido: ficam.filter((s) => s.falta_comprovante).length,
        voce_pode_aprovar: ficam.filter((s) => s.voce_pode_aprovar).length
      }
    })
  }, [])

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Saídas esperando decisão</h1>

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {feito !== null && (
          <p className="recado feito" role="status">{feito.recado}</p>
        )}

        {dados !== null && dados.total === 0 && (
          <p className="recado nada">
            Nenhuma saída esperando. Tudo o que foi lançado já tem decisão.
          </p>
        )}

        {dados !== null && dados.total > 0 && (
          <>
            <p className="explicacao">
              Estas saídas foram lançadas e <strong>ainda não foram decididas</strong>. Elas
              passaram do limite de quem lançou, e o dinheiro não anda até alguém com permissão
              decidir.
            </p>

            {/*
              O que esta tela faz e o que ela não faz, antes da lista e não depois.

              Quem abre aqui procura o botão de recusar junto com o de aprovar. Dizer de uma vez
              que recusar é no notebook é mais útil do que deixar a pessoa procurar.
            */}
            <p className="explicacao atencao">
              {dados.voce_pode_aprovar > 0 ? (
                <>
                  Aqui você <strong>aprova</strong>, e aprovar pede a sua senha.{' '}
                  {/*
                    O prazo sai da conta desta tela, recontada de quinze em quinze segundos, e
                    não do número que o servidor mandou quando a lista carregou. Achado TEL-001.
                  */}
                  {aSenhaJaVale
                    ? `Você já confirmou a senha, então pelos próximos ${minutosQueASenhaVale} minutos cada aprovação é um toque.`
                    : `Depois da primeira, as outras são um toque, por ${dados.minutos_da_senha} minutos.`}{' '}
                  <strong>Recusar continua sendo no notebook</strong> da congregação.
                </>
              ) : (
                <>
                  <strong>Nenhuma destas você pode decidir</strong>, e o motivo está escrito em
                  cada uma. Decidir continua sendo no notebook da congregação.
                </>
              )}
            </p>

            <p className="totais">
              {/*
                Só o NÚMERO vai em negrito, e não a palavra junto.

                `.totais strong` usa a letra de caneta, a mesma do dinheiro no papel. Com a
                palavra dentro, o retrato saiu com "4 saídas" em letra de máquina de escrever,
                do lado de "parado R$ 2.122,40", e as duas competiam.
              */}
              <span>
                esperando <strong>{dados.total}</strong>{' '}
                {dados.total === 1 ? 'saída' : 'saídas'}
              </span>
              <span>
                parado <strong>{emReais(dados.soma)}</strong>
              </span>
            </p>

            <ul className="saidas-esperando">
              {dados.saidas.map((saida) => (
                <Linha
                  key={saida.id}
                  saida={saida}
                  aSenhaJaVale={aSenhaJaVale}
                  minutosDaSenha={dados.minutos_da_senha}
                  aberto={aprovando === saida.id}
                  aoAbrir={() => setAprovando(saida.id)}
                  aoFechar={() => setAprovando(null)}
                  aoAprovar={(resultado) => {
                    setFeito(resultado)
                    setAprovando(null)
                    tirarDaLista(resultado)
                    anotarAJanela(resultado)
                    carregar()
                  }}
                  aJanelaFechou={aJanelaFechou}
                  aoSair={aoSair}
                />
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  )
}

/**
 * Uma saída na lista.
 *
 * O valor vem primeiro, e em negrito, porque é por ele que alguém decide se olha agora ou
 * depois. A categoria vem junto dele, no mesmo pedaço de linha, porque sozinho um valor não
 * diz nada.
 */
function Linha({
  saida,
  aSenhaJaVale,
  minutosDaSenha,
  aberto,
  aoAbrir,
  aoFechar,
  aoAprovar,
  aJanelaFechou,
  aoSair
}: {
  saida: SaidaEsperando
  aSenhaJaVale: boolean
  minutosDaSenha: number
  aberto: boolean
  aoAbrir: () => void
  aoFechar: () => void
  aoAprovar: (feito: SaidaAprovada) => void
  aJanelaFechou: () => void
  aoSair: () => void
}): JSX.Element {
  return (
    <li>
      <span className="nome">
        {emReais(saida.valor)}
        <small>{saida.categoria}</small>
      </span>

      {/*
        O que a tesouraria escreveu que foi aquilo. Pedido do Cristiano em 08/10/2026.

        **Numa linha só, cortada com reticências.** Descrição é texto livre, e já chegou saida
        com parágrafo inteiro dentro; sem o corte, uma linha comeria a tela e as outras saídas
        sumiriam para baixo. O corte é no CSS, e não aqui, porque assim o computador mostra mais
        do que o celular sem ninguém escolher um número de letras.
      */}
      <span className="detalhe descricao" title={saida.descricao}>
        {saida.descricao}
      </span>

      <span className="detalhe">
        {saida.congregacao.nome ?? 'congregação não identificada'} · gasto em {dia(saida.data)}
      </span>

      <span className="detalhe">
        {saida.lancado_por !== null
          ? `lançada por ${saida.lancado_por}`
          : 'quem lançou ainda não chegou do notebook'}{' '}
        · {aEspera(saida.dias_esperando)}
      </span>

      {/*
        A SAÍDA CORRIGIDA (0.3.24): estava aprovada, alguém corrigiu no notebook o valor, de onde
        saiu o dinheiro, de quem é a despesa ou quem pagou, e ela voltou para a fila. A marca é a
        mesma da folha do notebook, "corrigida por Fulano", com quem tinha aprovado ao lado.
      */}
      {saida.corrigida !== null && (
        <span className="detalhe corrigida-na-fila">
          <span className="etiqueta corrigida">corrigida por {saida.corrigida.por}</span>
          {saida.corrigida.aprovada_antes_por !== null && (
            <> Tinha sido aprovada por {saida.corrigida.aprovada_antes_por}; precisa de nova decisão.</>
          )}
        </span>
      )}

      {/*
        O aviso do comprovante. **Aviso, e nunca trava.**

        Falta de comprovante não impede lançar e não vai impedir aprovar: a única coisa que ela
        segura é o fechamento do mês. Quem decide sem saber disso descobre no fim do mês, e aí o
        mês já parou por um motivo que ninguém ligou a esta saída.
      */}
      {saida.falta_comprovante && (
        <span className="detalhe atencao">
          Falta o comprovante, e esta categoria exige. Enquanto ele não for anexado no notebook,
          esta saída <strong>não entra no fechamento do mês</strong>.
        </span>
      )}

      {saida.comprovante_exigido && saida.tem_comprovante && (
        <span className="detalhe">comprovante anexado</span>
      )}

      <Aprovar
        saida={saida}
        aSenhaJaVale={aSenhaJaVale}
        minutosDaSenha={minutosDaSenha}
        aberto={aberto}
        aoAbrir={aoAbrir}
        aoFechar={aoFechar}
        aoAprovar={aoAprovar}
        aJanelaFechou={aJanelaFechou}
        aoSair={aoSair}
      />
    </li>
  )
}

/**
 * O caminho de aprovar, dentro da linha. **Dois passos, de propósito.**
 *
 * Primeiro o botão, depois a confirmação. Nenhuma aprovação acontece a um toque de distância: é
 * a mesma forma da tela de Aguardando reabertura, e pelo mesmo motivo, com uma palavra trocada.
 * Lá a ação desfaz uma assinatura; aqui ela libera um pagamento.
 *
 * **A senha vai só na primeira.** Decisão do Cristiano, de 08/10/2026, depois de testar no
 * celular: depois de confirmada, ela vale por vinte minutos, e as outras aprovações da mesma
 * sentada são um toque. Quem tem dez saídas para aprovar não digita dez vezes num teclado de
 * celular, que é o jeito mais certo de a pessoa guardar a senha no navegador.
 *
 * **Quem decide se a senha ainda vale é o servidor, não esta página.** Ela só pergunta como
 * desenhar o formulário. Se a janela fechar entre carregar a lista e tocar no botão, a resposta
 * vem com `senha_necessaria`, e aí o campo aparece sem ninguém ter errado nada.
 *
 * **Quem não pode aprovar não vê botão, vê o motivo.** O motivo vem escrito do servidor; a tela
 * não monta frase nenhuma. Sem ele, quem só confere ficaria procurando um botão que nunca vai
 * existir para ela.
 */
function Aprovar({
  saida,
  aSenhaJaVale,
  minutosDaSenha,
  aberto,
  aoAbrir,
  aoFechar,
  aoAprovar,
  aJanelaFechou,
  aoSair
}: {
  saida: SaidaEsperando
  aSenhaJaVale: boolean
  minutosDaSenha: number
  aberto: boolean
  aoAbrir: () => void
  aoFechar: () => void
  aoAprovar: (feito: SaidaAprovada) => void
  aJanelaFechou: () => void
  aoSair: () => void
}): JSX.Element {
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  /**
   * A janela fechou no meio do caminho, e o servidor avisou.
   *
   * Quando isso acontece o campo de senha aparece, mesmo que a lista tivesse dito que a senha
   * ainda valia quando ela foi carregada.
   *
   * **Quem guarda esse fato é a página, e não esta linha.** Era aqui que ele morava até o achado
   * TEL-001, de 13/10/2026, e era por isso que o aviso do alto da tela continuava prometendo
   * aprovação com um toque enquanto este formulário já pedia a senha: a linha sabia, e não
   * contava para ninguém. A janela é uma só, da sessão, então o lugar dela é em cima.
   */
  const pedirASenha = !aSenhaJaVale

  if (!saida.voce_pode_aprovar) {
    return (
      <span className="detalhe nao-pode">
        {saida.recado_de_quem_nao_pode ?? 'Você não pode aprovar esta saída.'}
      </span>
    )
  }

  if (!aberto) {
    return (
      <button className="aprovar" onClick={aoAbrir}>
        Aprovar esta saída
      </button>
    )
  }

  async function aprovar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      aoAprovar(await servidor.aprovarSaida(saida.id, pedirASenha ? senha : undefined))
    } catch (falha: unknown) {
      if (ehSessaoTerminada(falha)) return aoSair()

      /*
         A janela de vinte minutos fechou enquanto a tela estava aberta.

         Isto não é erro de quem está usando: o campo aparece, com a frase do servidor em cima,
         e a pessoa segue de onde parou.
      */
      if (falha instanceof ErroDoServidor && falha.codigo === 'senha_necessaria') {
        aJanelaFechou()
      }

      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível aprovar.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-aprovacao" onSubmit={(e) => void aprovar(e)}>
      {/*
        O que vai acontecer, na hora de decidir, e não só no alto da tela.

        Quem rolou até aqui já passou por qualquer aviso de cima. Esta linha fica onde a pessoa
        está com o dedo no botão.
      */}
      <p className="recado atencao" role="note">
        Aprovar libera este pagamento. A saída vai para o notebook da congregação e sai desta
        lista.
        {saida.falta_comprovante && (
          <> E ela continua sem o comprovante que a categoria exige, então o mês não fecha
          enquanto ele não for anexado no notebook.</>
        )}
      </p>

      {pedirASenha ? (
        <>
          <label>
            Sua senha
            <input
              className="campo"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          {/* Dizer POR QUE ela é pedida, e por quanto tempo ela vale depois disso. */}
          <p className="explicacao">
            A senha é pedida porque esta é a única tela do painel que libera dinheiro. Depois de
            digitar, as próximas aprovações são só um toque, por {minutosDaSenha} minutos.
          </p>
        </>
      ) : (
        /*
          A senha ainda vale. Só o botão.

          A frase diz por que ela não está sendo pedida, senão a pessoa estranha a diferença
          entre uma aprovação e a seguinte e acha que o painel esqueceu de perguntar.
        */
        <p className="explicacao">
          Você já confirmou a sua senha há pouco, então esta vai com um toque.
        </p>
      )}

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button
          type="submit"
          className="perigo"
          disabled={indo || (pedirASenha && senha.length === 0)}
        >
          {indo ? 'Aprovando...' : `Aprovar ${emReais(saida.valor)}`}
        </button>
        <button type="button" onClick={aoFechar} disabled={indo}>
          Deixar esperando
        </button>
      </div>
    </form>
  )
}
